import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BookOpen } from 'lucide-react';

interface DescriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  thumbnail?: string;
}

// Sleek, theme-matched modal that shows a booklet's cover, full title and full
// description. Used by both the admin dashboard and the public library in place
// of the old inline "read more" expand.
const DescriptionModal: React.FC<DescriptionModalProps> = ({ isOpen, onClose, title, description, thumbnail }) => {
  const [imgFailed, setImgFailed] = React.useState(false);
  useEffect(() => { setImgFailed(false); }, [thumbnail, isOpen]);
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    // Lock background scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-dark/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[86vh]"
            initial={{ opacity: 0, scale: 0.96, y: 14 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 14 }}
            transition={{ type: 'spring', damping: 26, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              aria-label="Close"
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/80 backdrop-blur text-cool hover:text-dark hover:bg-warm/20 transition-colors shadow-sm"
            >
              <X size={18} />
            </button>

            {/* Cover thumbnail — shown as a floating book cover so you know what you're reading about */}
            <div className="w-full flex items-center justify-center pt-9 pb-4 px-6 bg-gradient-to-b from-warm/20 to-transparent flex-shrink-0">
              {thumbnail && !imgFailed ? (
                <img
                  src={thumbnail}
                  alt={title}
                  onError={() => setImgFailed(true)}
                  className="h-40 w-auto max-w-[70%] object-contain rounded-lg shadow-xl ring-1 ring-warm/30 bg-white"
                />
              ) : (
                <div className="h-40 w-32 rounded-lg bg-warm/15 ring-1 ring-warm/30 flex items-center justify-center">
                  <BookOpen className="text-cool/40" size={34} />
                </div>
              )}
            </div>

            <div className="px-6 md:px-8 pb-8 pt-2 overflow-y-auto">
              <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-warm">Publication</span>
              <h2 className="font-serif text-2xl md:text-3xl text-dark leading-tight mt-1.5 mb-4">{title}</h2>
              <p className="text-cool text-sm md:text-[15px] leading-relaxed whitespace-pre-line">
                {description || 'No description provided.'}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DescriptionModal;
